import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";

/** Badge variants available in the shared `notification-badge` global classes (ai-chat-cards.scss). */
type ConfidenceBadgeVariant = "success" | "warning" | "error";

/**
 * Confidence badge for the plan surfaces (plan-cards-spec §Confianza): a percentage pill without
 * bars, tinted by the confidence thresholds shared with `confidenceColor` (user ruling
 * 2026-10-11): ≥ 0.70 success, ≥ 0.50 warning, below 0.50 error. Styling comes entirely from the
 * global `notification-badge` classes and `mat-font-label-sm` — no component SCSS. When no
 * confidence value is known (`null`/`undefined`), the badge renders nothing.
 */
@Component({
  selector: "app-confidence-badge",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./confidence-badge.component.html",
})
export class ConfidenceBadgeComponent {
  /** Confidence value in [0..1]; `null`/`undefined` means the value is unknown and renders nothing. */
  readonly value = input<number | null>(null);

  /** Rounded percentage text (0.92 → 92), or `null` when no confidence value is known. */
  protected readonly percent = computed<number | null>(() => {
    const value: number | null | undefined = this.value();
    if (value === null || value === undefined) {
      return null;
    }
    return Math.round(value * 100);
  });

  /** Shared badge variant for the confidence thresholds, or `null` when no confidence value is known. */
  protected readonly variant = computed<ConfidenceBadgeVariant | null>(() => {
    const value: number | null | undefined = this.value();
    if (value === null || value === undefined) {
      return null;
    }
    if (value >= 0.7) {
      return "success";
    }
    if (value >= 0.5) {
      return "warning";
    }
    return "error";
  });
}